import { useEffect, useMemo, useState } from "react";

import { createColumnHelper } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import MatchedVehicles from "../../components/partRequests/MatchedVehicles";
import { PartRequestNetworkPill, PartRequestSourcePill, PartRequestStatusPill } from "../../components/partRequests/PartRequestPills";
import ButtonGroup from "../../components/shared/ButtonGroup";
import PagedTable from "../../components/shared/Tables/PagedTable";
import { useAuth } from "../../contexts/AuthContext";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import usePartRequests, { PART_REQUESTS_PAGED_URL } from "../../data/usePartRequests";
import { IPartRequest, PartRequestSource } from "../../models/partRequests/IPartRequest";
import formatDateTime from "../../utils/formatDateTime";
import {
  buildPartRequestFilter,
  partRequestVehicleLabel,
  PartRequestSourceFilter,
  PartRequestStatusFilter,
} from "../../utils/partRequests";

const colHelper = createColumnHelper<IPartRequest>();

const sourceOptions: { id: PartRequestSourceFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "partpro", label: "PartPro" },
  { id: "internal", label: "Internal" },
];

const statusOptions: { id: PartRequestStatusFilter; label: string }[] = [
  { id: "live", label: "Live" },
  { id: "open", label: "Open" },
  { id: "claimed", label: "Claimed" },
  { id: "fulfilled", label: "Fulfilled" },
  { id: "closed", label: "Closed" },
  { id: "all", label: "Everything" },
];

export default function PartRequests() {
  const navigate = useNavigate();
  const { setInfo } = usePageNameContext();
  const { claims } = useAuth();
  const { sync, integrationStatus, partProConnected } = usePartRequests();

  const [source, setSource] = useState<PartRequestSourceFilter>("all");
  const [status, setStatus] = useState<PartRequestStatusFilter>("live");
  const [onlyMatched, setOnlyMatched] = useState(false);
  const [onlyMine, setOnlyMine] = useState(false);

  useEffect(() => {
    setInfo({
      name: "Part Requests",
      desc: "Requests from PartPro buyers and from callers the yard couldn't supply - see what's in stock to match, claim them and message the buyer.",
    });
  }, []);

  const additionalBody = useMemo(
    () => buildPartRequestFilter(source, status, { onlyMatched, onlyMine }),
    [source, status, onlyMatched, onlyMine]
  );

  const cols = useMemo(
    () => [
      colHelper.accessor("requestedAt", {
        header: "Requested",
        enableSorting: true,
        cell: (info) => (
          <div>
            <p className="whitespace-nowrap">{formatDateTime(info.getValue(), DateTime.DATE_SHORT)}</p>
            <p className="text-xs text-gray-600">{DateTime.fromISO(info.getValue()).toRelative()}</p>
          </div>
        ),
      }),
      colHelper.accessor("source", {
        header: "Source",
        enableSorting: false,
        cell: (info) => (
          <div className="flex flex-col items-start gap-1">
            <PartRequestSourcePill source={info.getValue()} />
            <PartRequestNetworkPill request={info.row.original} />
          </div>
        ),
      }),
      colHelper.accessor("partWanted", {
        header: "Part wanted",
        enableSorting: true,
        cell: (info) => (
          <div className="max-w-md">
            <p className="font-medium text-gray-900">{info.getValue()}</p>
            {info.row.original.details && (
              <p className="line-clamp-2 text-xs text-gray-600">{info.row.original.details}</p>
            )}
          </div>
        ),
      }),
      colHelper.accessor("make", {
        header: "Vehicle",
        enableSorting: true,
        cell: (info) => <span className="whitespace-nowrap">{partRequestVehicleLabel(info.row.original)}</span>,
      }),
      colHelper.display({
        id: "matches",
        header: "In the yard",
        cell: (info) => <MatchedVehicles matches={info.row.original.matchedVehicles} compact />,
      }),
      colHelper.accessor("status", {
        header: "Status",
        enableSorting: true,
        cell: (info) => {
          const request = info.row.original;
          return (
            <div className="flex flex-col items-start gap-1">
              <PartRequestStatusPill status={info.getValue()} />
              {request.claimedOn && (
                <span className="text-xs text-gray-500">
                  Claimed {formatDateTime(request.claimedOn, DateTime.DATE_SHORT)}
                </span>
              )}
              {request.source === PartRequestSource.Internal && request.customerName && (
                <span className="text-xs text-gray-500">{request.customerName}</span>
              )}
            </div>
          );
        },
      }),
    ],
    []
  );

  return (
    <div className="mt-4">
      {integrationStatus.isSuccess && !partProConnected && (
        <div className="mb-4 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
          PartPro is not connected, so requests from PartPro buyers are not being pulled in and internal requests
          can't be sent to the network. Internal requests still work.
          {claims?.isAdmin && (
            <>
              {" "}
              <button type="button" onClick={() => navigate("/admin/connections")} className="font-medium underline">
                Connect PartPro under Auth Connections
              </button>
              .
            </>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-end gap-6">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Source</label>
            <ButtonGroup
              size="sm"
              options={sourceOptions.map((o) => ({ id: o.id, label: o.label, value: o.id }))}
              selectedIndex={sourceOptions.findIndex((o) => o.id === source)}
              onChange={(id) => setSource(id as PartRequestSourceFilter)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Status</label>
            <ButtonGroup
              size="sm"
              options={statusOptions.map((o) => ({ id: o.id, label: o.label, value: o.id }))}
              selectedIndex={statusOptions.findIndex((o) => o.id === status)}
              onChange={(id) => setStatus(id as PartRequestStatusFilter)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="cursor-pointer" checked={onlyMatched} onChange={(e) => setOnlyMatched(e.target.checked)} />
            Only with a vehicle in the yard
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="cursor-pointer" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />
            Claimed by me
          </label>
        </div>
      </div>

      <PagedTable<IPartRequest>
        url={PART_REQUESTS_PAGED_URL}
        columns={cols}
        queryLabel="Search part, vehicle or customer"
        additionalBody={additionalBody}
        initialSort={[{ id: "requestedAt", desc: true }]}
        onRowClick={(request) => navigate(`/part-requests/${request.id}`)}
        rowColor={(request) => (request.matchedVehicles.length > 0 && !request.claimedOn ? "bg-green-50" : "")}
        actionButtons={[
          { label: "Log internal request", onClick: () => navigate("/part-requests/new") },
          ...(claims?.isAdmin && partProConnected
            ? [{ label: "Refresh from PartPro", onClick: () => sync.mutate(), isLoading: sync.isLoading, classes: "!bg-white border border-gray-300 !text-gray-700 hover:!bg-gray-50" }]
            : []),
        ]}
        showTotal
      />
    </div>
  );
}
